import './styles.scss'
import useWindowSize from '../hooks/useWindowSize'
import classNames from 'classnames'
import React, { useEffect, useState } from 'react'
import TopUsageGraph from './TopUsageGraph'
import YearlyUsageGraph from './YearlyUsageGraph'
import ReactTooltip from 'react-tooltip';
import MiniMap from './MiniMap'
import Switch from './Switch'
import Checkbox from './Checkbox'
import InfoIcon from './InfoIcon'
import ScopePicker from './ScopePicker'
import YearPicker from './YearPicker'
import incompleteCountries from '../hooks/incompleteCountries'
import { Link } from 'react-router-dom'
import { finalYear } from 'App'
const ExLink = ({href, children}) => {
  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
}
const energyTypes = ['Fossil Fuel', 'Clean', 'Other']
const fossilFuelSubTypes = ['CCS', 'Coal', 'Gas','Hydrogen', 'Oil', 'Oil and Gas']
const g20 = ['Argentina', 'Australia', 'Brazil', 'Canada', 'China', 'France', 'Germany', 'India', 'Indonesia', 'Italy', 'Japan', 'Mexico', 'Russia', 'Saudi Arabia', 'South Africa', 'Korea', 'Turkey', 'United Kingdom', 'United States', 'the African Union', 'EU']

incompleteCountries.forEach(country => {
  if(g20.includes(country)) {
    g20[g20.indexOf(country)] = country + '*'
  }
})

export const colors = {
  // OLD COLORS
  // 'Fossil Fuel': '#EFC1A8',
  // 'Clean': '#99DEE3',
  // 'Other': '#6ABEF0',
  // NEW COLORS
  'Fossil Fuel': '#04000A',
  'Clean': '#A4BA45',
  'Other': '#8492DC',
}
export default function DataView(props) {
  const downloadPath = `OCI Public Finance for Energy Database - Download Copy.xlsx`
  const ccsLink = `https://docs.google.com/spreadsheets/d/1eiwqktp_LzMK3wYYZe71KzlAF7WCMOfv545tzZC96eA/`
  const { data, headerHeight } = props
  const [selectedEnergyTypes , setSelectedEnergyTypes] = useState(['Fossil Fuel'])
  const [selectedfossilFuelSubTypes , setSelectedfossilFuelSubTypes] = useState(['Coal', 'Oil', 'Gas', 'Oil and Gas', 'CCS', 'Hydrogen'])
  const [aggregationType, setAggregationType] = useState('sum')
  const [yearType, setYearType] = useState('custom')
  const [customYears, setCustomYears] = useState([2017, finalYear])
  const [scopeType, setScopeType] = useState('both')
  const sections = [
    {
      title: 'Public Finance by Year',
      orderMultiColumnView: 0,
      description: <React.Fragment>This includes finance by year and energy type, from MDBs, G20 ECAs, G20 DFIs, G20 NDBs, and national governments. You can use the top dashboard to select just international or domestic finance, as well as to include or exclude the subsidies. Filter to select a specific country or MDB. Annual financing totals and data availability are often variable at individual institutions, so please use filtered results with caution. Read our <Link to='/about'>About page</Link> for more information on our data and methodology.</React.Fragment>,
      content: (
        data.length ? <YearlyUsageGraph data={data} /> : null
      )
    },
    {
      title: 'Top 15 G20 Country Comparison',
      orderMultiColumnView: 1,
      description: 'The 15 countries with the largest flows of international and domestic public finance for energy as well as domestic subsidies for CCS and fossil hydrogen, based on your dashboard selections. This includes finance from bilateral ECAs and DFIs, but not G20 country contributions to MDBs, as these cannot be disaggregated. Hover over each country label to see the institutions included.',
      content: (
        <TopUsageGraph data={data.filter(d => d.institutionKind !== 'Multilateral' && g20.includes(d.institutionGroup) )} />
      )
    },
    {
      title: 'Multilateral Development Banks Comparison',

      orderMultiColumnView: 3,
      description: 'Public energy finance from the major MDBs. Note that G20 countries are collectively major shareholders of each of these institutions, but that many other countries hold shares as well. Hover over each bank label to see the institutions included.',
      content: (
        <TopUsageGraph isBank data={data.filter(d => d.institutionKind === 'Multilateral')} />
      )
    },

    {
      title: 'Export Credit Agencies Comparison',

      orderMultiColumnView: 5,
      description: 'Public energy finance from G20 ECAs, by country. ECAs are focused on trade finance and typically have a mandate to promote the export of goods and services from their country. Hover over each country label to see the institutions included.',
      content: (
        <TopUsageGraph data={data.filter(d => d.institutionKind === 'Export Credit')} />
      )
    },

    {
      title: 'Development Finance Institutions Comparison',
      orderMultiColumnView: 2,
      description: 'Public energy finance from G20 DFIs, by country. Hover over each country label to see the institutions included.',
      column: 'right',
      content: (
        <TopUsageGraph data={data.filter(d => d.institutionKind === 'Bilateral')} />
      )
    },
    {
      title: 'Public Finance by Country',
      column: 'left',
      orderMultiColumnView: 4,
      description: `Where G20 and MDB public finance for energy is flowing, grouped either by recipient or financing country (use toggle below). Click on “Clean” or Other” to see non-fossil fuel finance flows. Hover over each country to see flow amounts. For domestic finance and CCS and fossil hydrogen subsidies, the recipient and financing country will be the same.`,
      content: (
        data.length ? <MiniMap data={data} /> : null
      )
    },
  ]
  sections.forEach((s, i) => s.index = i)

  const {width} = useWindowSize()

  useEffect(() => {
    ReactTooltip.rebuild()
  })

  useEffect(() => {
    ReactTooltip.rebuild()
  })

  if (!width) {
    return null
  }

  const maxSize = width > 1850
  const singleColumnView = 650 <= width && width < 1100
  const stackedView = width < 650

  // This section width determines size of the svg on the data dashboard
  const sectionWidth = singleColumnView ? ((width - 320) * .85) 
    : maxSize ? (715)
    : stackedView ? (width*.8)
    : ((width - 400) * .925) / 2
  const renderSection = (section) => {
    const description = section.description
    let defaultContent = <svg width={sectionWidth} height={200 + section.index * 50} />
    let content = section.content ?
      React.cloneElement(section.content,
        {
          width: sectionWidth,
          height: 200,
          selectedEnergyTypes,
          selectedfossilFuelSubTypes,
          aggregationType,
          yearType,
          customYears,
          scopeType,
        }) :
      defaultContent
    return (
      <section key={section.title}>
        <h2>{section.title}
        </h2>
        <div className='description'>{description}</div>
        {content}
      </section>
    )
  }
  let sectionDivs = sections.map(renderSection)

  if (!singleColumnView && !stackedView) {
    const sorted = [...sections]
    sorted.sort((a, b) => a.orderMultiColumnView - b.orderMultiColumnView)
    sectionDivs = sorted.map(renderSection)
  }

  return (
    <div style={{ marginTop: headerHeight }}>
      <div className='dataViewIntro'>
        <h1>Data Dashboard</h1>
        <div className='description'>
<p>This dashboard shows the total flows of: </p>
<ul>
<li>Fossil fuel, clean, and other energy projects from G20 countries’ export credit agencies (ECAs), development finance institutions (DFIs), between 2013 and 2025</li>
<li>Fossil fuel, clean, and other energy projects from G20 countries’ national development banks (NDBs), between 2016 and 2024</li>
<li>Fossil fuel, clean, and other energy projects from major multilateral development banks (MDBs)</li>
<li>Carbon capture and storage (CCS) and fossil hydrogen domestic government subsidies</li>
</ul>
<p>The public finance dataset captures completed expenditures. The CCS and fossil hydrogen dataset captures the amount governments have pledged to specific projects by announcement year; the final amount dispersed may be lower. Further details on our methodology are available on the <Link to="/about">About page</Link>.</p>
      <p className="download-instructions">To explore the data at the transaction level, download the dataset as a spreadsheet:</p>
      <div className="download-container">
        <a href={`${process.env.PUBLIC_URL}/${downloadPath}`} className="download-button">Public finance dataset</a>
        <ExLink href={ccsLink} className="download-button">CCS and Fossil Hydrogen dataset</ExLink>
      </div>

<p>An asterisk (*) indicates that very limited or no direct reporting is available from this country or institution.</p>
        </div>
      </div>
      <div className = "dataAndControls">
        <div className='controls' style={{ top: headerHeight }}>

          <YearPicker
            value={yearType}
            onChange={e => setYearType(e.target.value)}
            customYears={customYears}
            setCustomYears={setCustomYears}
          />
          <ScopePicker
            value={scopeType}
            onChange={e => setScopeType(e.target.value)}
          />

          <div className='energyTypes'>
            <p className="control-label">Energy types:</p>
            <div className="energy-picker">
              {energyTypes.map(type => {
                const color = colors[type]
                const enabled = selectedEnergyTypes.includes(type)
                const isOther = type === 'Other'
                const dataTip = isOther ? `<div><div style="font-weight: bold">Other energy type:</div>This includes large hydropower, nuclear, and biomass projects as well as energy spending that cannot be specifically linked to a source of energy (most frequently this is for electricity transmission and distribution projects).</div>` : null
                return (
                  <>
                  <label className='energyTypeToggle' key={type} style={{ backgroundColor: color,  opacity: enabled ? null : 0.35 }} onClick={ () => {

                      const newSelected = selectedEnergyTypes.includes(type) ?
                        selectedEnergyTypes.filter(t => t !== type) :
                        [...selectedEnergyTypes, type]
                      if (newSelected.length === 0) {
                        return selectedEnergyTypes
                      }
                      newSelected.sort((a, b) => {
                        const aIndex = energyTypes.indexOf(a)
                        const bIndex = energyTypes.indexOf(b)
                        return aIndex - bIndex
                      })

                      setSelectedEnergyTypes(newSelected)
                  }}>
                    <Checkbox
                      checked={enabled}
                    />
                    {type}
                    {isOther ?
                      <InfoIcon dataHtml={true} dataTip={dataTip} style={{ marginLeft: '1em', transform: 'translateY(1px)'}} /> : null
                    }
                  </label>
                  {type === 'Fossil Fuel' && (
                    <div className='fossilFuelSubTypes'>
                      {fossilFuelSubTypes.map(type => {
                        const enabled = selectedfossilFuelSubTypes.includes(type)
                        const isOther = type === 'Other'
                        const dataTip = isOther ? `<div><div style="font-weight: bold">Other energy type:</div>This includes large hydropower, nuclear, and biomass projects as well as energy spending that cannot be specifically linked to a source of energy (most frequently this is for electricity transmission and distribution projects).</div>` : null
                        return (
                          <label className='fossilFuelSubTypeToggle' key={type} style={{ backgroundColor: '#000000', color:'#fff',  opacity: selectedEnergyTypes.includes('Fossil Fuel') && enabled ? null : 0.35 }} onClick={ () => {

                              const newFossilFuelSubtypesSelected = selectedfossilFuelSubTypes.includes(type) ?
                                selectedfossilFuelSubTypes.filter(t => t !== type) :
                                [...selectedfossilFuelSubTypes, type]
                              if (newFossilFuelSubtypesSelected.length === 0) {
                                return selectedfossilFuelSubTypes
                              }
                              newFossilFuelSubtypesSelected.sort((a, b) => {
                                const aIndex = energyTypes.indexOf(a)
                                const bIndex = energyTypes.indexOf(b)
                                return aIndex - bIndex
                              })

                              setSelectedfossilFuelSubTypes(newFossilFuelSubtypesSelected)
                          }}>
                            <Checkbox
                              checked={enabled}
                            />
                            {type}
                            {isOther ?
                              <InfoIcon dataHtml={true} dataTip={dataTip} style={{ marginLeft: '1em', transform: 'translateY(1px)'}} /> : null
                            }
                          </label>

                        )
                      })}
                    </div>
                  )}
                  </>
                )
              })}
            </div>
          </div>
          <Switch
            label=''
            label1='Total Sum'
            label2='Average Annual'
            value={aggregationType === 'sum'}
            toggle={() => setAggregationType(aggregationType === 'average' ? 'sum' : 'average')}
            intro={true}
          />
        </div>
        <div className={classNames('DataView', { twoColumnView: !singleColumnView && !stackedView  })}>
          {sectionDivs}
        </div>
      </div>
      <div className='limited-reporting'>
        * Limited or no direct reporting available. To read about how our data is sourced, see the <Link to='/about'>About page</Link>.
      </div>
    </div>
  )
}
